import type { Vec2 } from '../../geo/types';
import type { Rng } from '../../core/rng';
import { distance, type StreetGraph } from './streetGraph';
import type { LifeClearance } from './routes';
import { pedestrianPath } from './pedestrianPath';
import { corners, doorTravel, heading, stay, type Access } from './itinerary';
import type { LifeRoute, LifeStep } from './types';

/*
 * A few burglars in the dead of night (the user's request of 7 October 2026: "some shady burglars
 * sneaking around"). They are an invention for the scene, not a record: no source read here counts
 * or describes the thieves of 1538. Each lurks at a corner, creeps along a back street the watch
 * does not walk, stops to look behind him, tries a door, is inside a while and creeps back the way
 * he came. He carries no lantern, which the ordinance required of anyone abroad (docs/NIGHT.md).
 * Every number here is an estimate.
 */
export const BURGLARS = {
  count: 6,
  /** Minutes after midnight between which they are abroad: from eleven until half past four. */
  fromMinutes: 23 * 60,
  untilMinutes: 4.5 * 60,
  /** Least metres between a burglar's way and any street the watch walks. */
  clearOfWatchM: 45,
  /** Least metres between the houses two of them go for. */
  apartM: 160,
  /** Metres of street between where one lurks and the house he goes for: least and most. */
  wayM: [45, 130],
  /** Metres a second: a creep, well under a walk. */
  speed: 0.5,
  /** Metres from the middle of the street, towards the wall on his right. */
  lane: 1.7,
  /** Metres he creeps before he stops to look about him. */
  dashM: 20,
} as const;

function look(at: Vec2, facing: number, seconds: number): LifeStep {
  return { from: at, to: at, seconds, action: 'wait', heading: facing };
}

/* Along the points in dashes, a halt after each: a look behind, then one ahead at the corners. */
function creep(steps: LifeStep[], points: readonly Vec2[], rng: Rng): void {
  for (let i = 1; i < points.length; i++) {
    const from = points[i - 1]!,
      to = points[i]!,
      length = distance(from, to);
    if (length < 0.01) continue;
    const dashes = Math.max(1, Math.ceil(length / BURGLARS.dashM));
    for (let d = 1; d <= dashes; d++) {
      const a: Vec2 = [
          from[0] + ((to[0] - from[0]) * (d - 1)) / dashes,
          from[1] + ((to[1] - from[1]) * (d - 1)) / dashes,
        ],
        b: Vec2 =
          d === dashes
            ? to
            : [
                from[0] + ((to[0] - from[0]) * d) / dashes,
                from[1] + ((to[1] - from[1]) * d) / dashes,
              ];
      steps.push({
        from: a,
        to: b,
        seconds: distance(a, b) / BURGLARS.speed,
        action: 'walk',
        heading: heading(a, b),
      });
      const last = i === points.length - 1 && d === dashes;
      if (!last) steps.push(look(b, heading(b, a) + rng.range(-0.5, 0.5), rng.range(3, 8)));
    }
  }
}

const duration = (steps: readonly LifeStep[]): number =>
  steps.reduce((sum, step) => sum + step.seconds, 0);

/** The burglars' rounds, among the `homes` nobody of the cast lives in or visits by night. */
export function burglarRoutines(
  graph: StreetGraph,
  homes: readonly Access[],
  clearance: LifeClearance,
  patrolled: readonly Vec2[],
  rng: Rng
): LifeRoute[] {
  const clear = (p: Vec2): boolean =>
    patrolled.every((walked) => distance(p, walked) >= BURGLARS.clearOfWatchM);
  const routes: LifeRoute[] = [],
    chosen: Access[] = [];
  /* The houses in an order of the seed's own, so the burglars are not all in one quarter. */
  const order = homes.map((home) => ({ home, key: rng.next() })).sort((a, b) => a.key - b.key);
  for (const { home } of order) {
    if (routes.length >= BURGLARS.count) break;
    if (!clear(home.approach)) continue;
    if (chosen.some((other) => distance(other.approach, home.approach) < BURGLARS.apartM)) continue;
    /* He lurks as far off along the streets as the way allows, out of the watch's sight. */
    let start = -1,
      far: number = BURGLARS.wayM[0];
    for (const [node, along] of graph.within(home.node, BURGLARS.wayM[1]))
      if (along > far && clear(graph.nodes[node]!.position)) [start, far] = [node, along];
    const path = start < 0 ? null : graph.path(start, home.node);
    if (!path || path.length < 3 || !path.every(clear)) continue;
    const there = corners(pedestrianPath([...path, home.approach], clearance, BURGLARS.lane)),
      back = corners(
        pedestrianPath([home.approach, ...[...path].reverse()], clearance, BURGLARS.lane)
      );
    const steps: LifeStep[] = [look(there[0]!, heading(there[0]!, there[1]!), rng.range(15, 30))];
    creep(steps, there, rng);
    stay(steps, home, rng.range(8, 14), 'wait');
    doorTravel(steps, home, false, BURGLARS.speed);
    stay(steps, home, rng.range(45, 90), 'inside');
    doorTravel(steps, home, true, BURGLARS.speed);
    stay(steps, home, rng.range(4, 7), 'wait');
    creep(steps, back, rng);
    chosen.push(home);
    routes.push({
      id: `burglar-night-${routes.length}`,
      role: 'burglar',
      streetId: home.entry.streetId,
      a: there[0]!,
      b: home.approach,
      radius: 0.48,
      speed: BURGLARS.speed,
      scale: rng.range(0.94, 1.0),
      phase: rng.range(0, duration(steps)),
      routine: steps,
      night: true,
      late: true,
    });
  }
  return routes;
}
